<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import NextButton from 'dashboard/components-next/button/Button.vue';
import tiktokAdsAPI from 'dashboard/api/tiktokAds';

const store = useStore();

const connection = ref(null);
const targetInboxId = ref('');
const conversionIdentifier = ref('');
const isSaving = ref(false);

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const webhookUrl = 'https://app.smart2.com.br/webhooks/tiktok_leadgen';

const fetchConnection = async () => {
  try {
    const { data } = await tiktokAdsAPI.get();
    connection.value = (data || [])[0] || null;
    if (connection.value) {
      targetInboxId.value = connection.value.target_inbox_id || '';
      conversionIdentifier.value = connection.value.conversion_identifier || '';
    }
  } catch (error) {
    connection.value = null;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchConnection();
});

const save = async () => {
  if (!connection.value) return;
  isSaving.value = true;
  try {
    await tiktokAdsAPI.update(connection.value.id, {
      target_inbox_id: targetInboxId.value || null,
      conversion_identifier: conversionIdentifier.value || null,
    });
    toast.success('Configuração salva.');
  } catch (error) {
    toast.error('Não foi possível salvar.');
  } finally {
    isSaving.value = false;
  }
};

const copyWebhook = () => {
  navigator.clipboard?.writeText(webhookUrl);
  toast.success('URL copiada.');
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">Lead Ads</h1>
      <p class="mt-1 text-sm text-n-slate-10">
        Leads dos formulários do TikTok entram automáticos no CRM (viram contato
        + disparam as automações de conversão), igual ao Meta Lead Ads.
      </p>
    </div>

    <div
      v-if="!connection"
      class="p-4 text-sm rounded-lg text-n-amber-11 bg-n-amber-3"
    >
      Conecte o TikTok primeiro na aba Conexões.
    </div>

    <template v-else>
      <!-- Webhook URL -->
      <div class="flex flex-col gap-2 p-4 rounded-xl border border-n-weak">
        <span class="text-sm font-medium text-n-slate-12">
          URL do Webhook (registre no portal do app TikTok)
        </span>
        <div class="flex items-center gap-2">
          <code
            class="flex-1 px-3 py-2 text-xs rounded-lg bg-n-alpha-black2 text-n-slate-11 truncate"
          >
            {{ webhookUrl }}
          </code>
          <NextButton
            icon="i-lucide-copy"
            label="Copiar"
            faded
            sm
            @click="copyWebhook"
          />
        </div>
        <p class="text-[11px] text-n-slate-9">
          No TikTok For Business → seu app → Webhooks, aponte para esta URL e
          assine os eventos de Lead Generation.
        </p>
      </div>

      <!-- Settings -->
      <div class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak">
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Caixa de entrada de destino (opcional)
          <select
            v-model="targetInboxId"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
          >
            <option value="">— não criar conversa —</option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-10">
          Identificador de conversão (opcional)
          <input
            v-model="conversionIdentifier"
            type="text"
            class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
            placeholder="Ex: tiktok-lead (vazio = tiktok-lead-<form>)"
          />
        </label>
        <div class="flex justify-end">
          <NextButton label="Salvar" sm :is-loading="isSaving" @click="save" />
        </div>
      </div>
    </template>
  </div>
</template>
